import React, { useState, useEffect } from 'react';

import { ControlledTextField, ErrorMessage, Loading, StatusMessage } from '@/src/ui';
import { apiRequest } from '@/src/services';
import { Constants } from '@/src/utils';

interface ExamTitleFieldProps {
    id?: number;
    title: string;
    control: any;
    setIsTitleError: (value: boolean) => void;
    error: any;
}

interface VerifyTitleResponse {
    isValid: boolean;
}

// Handles checking whether the title is unique or not
const ExamTitleField: React.FC<ExamTitleFieldProps> = (props) => {
    const { id, title, control, setIsTitleError, error } = props;

    const [loading, setLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [conflict, setConflict] = useState('');

    const [initialLoadComplete, setInitialLoadComplete] = useState(false);

    useEffect(() => {
        const handler = setTimeout(async () => {
            if (!title) return;
            // Skip the first load
            if (!initialLoadComplete) {
                setInitialLoadComplete(true);
                return;
            }

            let apiPath = `assessments/exams/verifyTitle?title=${title}`;
            if (id) apiPath += `&id=${id}`;

            try {
                setLoading(true);
                const res = await apiRequest<VerifyTitleResponse>('get', apiPath);
                if (res.data.isValid) {
                    setConflict('');
                    setSuccessMessage('Exam title is available.');
                    setIsTitleError(false);
                } else {
                    setConflict('Another exam with this title already exists.');
                    setSuccessMessage('');
                    setIsTitleError(true);
                }
            } catch {
                setConflict('Error checking title.');
                setSuccessMessage('');
                setIsTitleError(true);
            } finally {
                setLoading(false);
            }
        }, 500);
        return () => clearTimeout(handler);
    }, [id, title, setIsTitleError, initialLoadComplete]);

    return (
        <div>
            <ControlledTextField
                name="title"
                control={control}
                label="Assessment"
                placeholder="Enter exam title"
                error={error}
                maxLength={Constants.MAX_CHARS.TITLE}
            />
            <Loading visible={loading} message="Checking exam title..." />
            <ErrorMessage error={conflict} />
            <StatusMessage visible={!!successMessage} status={successMessage} />
        </div>
    );
};

export default ExamTitleField;
